import type React from 'react';
import type { Bar } from 'react-chartjs-2';
import type { ColorTheme } from './graph';
import { BORDER_STYLES, COLOR_THEMES } from './graph';

type LeadtimeRange = 'nullable' | 'short_notice' | 'normal_booking' | 'early_booking' | 'planned_booking';

const LEADTIME_RANGE_LABELS: Record<LeadtimeRange, string> = {
  nullable: '不明',
  short_notice: '間際予約',
  normal_booking: '通常予約',
  early_booking: '早期予約',
  planned_booking: '計画予約'
};

export interface BarLeadtime {
  key: LeadtimeRange;
  count: number;
  price?: number;
}

export const chartOptions: React.ComponentProps<typeof Bar>['options'] = {
  maintainAspectRatio: false,
  responsive: false,
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      callbacks: {
        label: tooltipItem => {
          const value = tooltipItem.raw as number;
          return tooltipItem.datasetIndex === 0
            ? `${value.toLocaleString()}件`
            : utils.formatPrice(Math.round(value));
        }
      }
    }
  }
};

export const getBarDataLeadtimes = (leadtimes: BarLeadtime[], theme: ColorTheme = 'default') => {
  const labels = leadtimes.map(({ key }) => LEADTIME_RANGE_LABELS[key]);
  const counts = leadtimes.map(({ count }) => count);
  const prices = leadtimes.map(({ price }) => price ?? 0);
  const usePrices = prices.some(value => value > 0);
  const colorTheme = COLOR_THEMES[theme];
  const borderStyle = BORDER_STYLES[theme];

  const options: React.ComponentProps<typeof Bar>['options'] = {
    scales: {
      yAxesCounts: {
        stacked: false,
        position: 'left'
      },
      xAxesCounts: {
        grid: { display: false }
      }
    }
  };

  const datasets: React.ComponentProps<typeof Bar>['data']['datasets'] = [
    {
      type: 'bar',
      yAxisID: 'yAxesCounts',
      label: '旅程数',
      data: counts,
      backgroundColor: [
        colorTheme.red.backgroundColor,
        colorTheme.yellow.backgroundColor,
        colorTheme.green.backgroundColor,
        colorTheme.blue.backgroundColor
      ],
      ...borderStyle
    }
  ];

  if (usePrices) {
    Object.assign(options.scales ?? {}, {
      yAxesPrices: {
        stacked: false,
        position: 'right'
      }
    });

    datasets.push({
      type: 'bar',
      yAxisID: 'yAxesPrices',
      label: '相場',
      data: prices,
      ...colorTheme.green,
      ...borderStyle
    });
  }

  return { data: { labels, datasets }, options };
};
